import { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Divider,
    IconButton,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tab,
    Typography,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import StatsSummary from "../StatsSummary";
import PageLink from "../common/PageLink";
import VideoPlayer from "../VideoPlayer";
import RunSteps from "../testRun/RunSteps";
import TestRunAssertion from "../testRun/TestRunAssertion";
import TraceTab from "../../pages/TestRun/components/TraceTab";
import {
    getTestRunCycleLabel,
    getTestRunVersionLabel,
} from "../../utils/formatTestRunContext";
import { formatDate } from "../../utils/formatDate";
import { shouldShowTestGenerationDebug } from "../../utils/addExternalScripts";
import {
    formatTokenCount,
    formatUsdCost,
    getDiscoveryDebugSummary,
} from "../../utils/discoveryDebug";

const statusStyles = {
    PENDING: { backgroundColor: "#F0AD4E", color: "#fff" },
    RUNNING: { backgroundColor: "#4574D2", color: "#fff" },
    COMPLETED: { backgroundColor: "#198754", color: "#fff" },
    FAILED: { backgroundColor: "#D32F2F", color: "#fff" },
    INITIAL_FAILURE: { backgroundColor: "#8E1515", color: "#fff" },
    CANCELLED: { backgroundColor: "#F8DF73", color: "#2D4356" },
};

const EmbeddedTestRun = ({
    testRun,
    testCase,
    loading = false,
    onBack = null,
    onOpenInNewTab = null,
    hideHeader = false,
    isDraft = false,
}) => {
    const videoRef = useRef(null);
    const [activeTabValue, setActiveTabValue] = useState("1");
    const [activeStep, setActiveStep] = useState(null);
    const [videoTime, setVideoTime] = useState(0);
    const [showDebug, setShowDebug] = useState(false);
    const debugSummary = getDiscoveryDebugSummary(testRun?.discovery_debug);

    useEffect(() => {
        if (testRun?.steps?.length) {
            setActiveStep(testRun.steps[0].id);
        }
    }, [testRun?.id, testRun?.steps]);

    useEffect(() => {
        (async () => {
            try {
                if (typeof window !== "undefined" && "TREEGRESS_SHOW_DEBUG" in window) {
                    setShowDebug(Boolean(window.TREEGRESS_SHOW_DEBUG));
                } else {
                    setShowDebug(await shouldShowTestGenerationDebug());
                }
            } catch {
                setShowDebug(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (activeTabValue === "1" && videoRef.current) {
            videoRef.current.currentTime = videoTime;
        }
    }, [activeTabValue, videoTime]);

    const handleChangeActiveTab = (_, newValue) => {
        if (activeTabValue === "1" && videoRef.current) {
            setVideoTime(videoRef.current.currentTime);
        }
        setActiveTabValue(newValue);
    };

    const handleSeek = (time) => {
        if (videoRef.current) {
            videoRef.current.currentTime = time;
        }
    };

    const renderStatusLabel = (status) => {
        if (!status) return "";
        if (status === "RUNNING") return "In Progress...";
        if (status === "COMPLETED") return "Passed";
        if (status === "INITIAL_FAILURE") return "Initial failure";
        return status.toLowerCase();
    };

    if (loading) {
        return (
            <Typography sx={{ mt: 2 }} color="text.secondary">
                Loading test run...
            </Typography>
        );
    }

    if (!testRun && !isDraft) {
        return (
            <Typography sx={{ mt: 2 }} color="text.secondary">
                No test run selected.
            </Typography>
        );
    }

    const handleOpenInNewTab = () => {
        if (typeof onOpenInNewTab === "function" && testRun?.id) {
            onOpenInNewTab(testRun.id);
        }
    };

    const hasFinished =
        testRun?.status === "COMPLETED" ||
        testRun?.status === "FAILED" ||
        testRun?.status === "INITIAL_FAILURE";

    return (
        <Box className="testRun embeddedTestRun">
            <Box className="testRunContentContainer">
                {!hideHeader && (
                    <Box className="testRunTopSection" sx={{ mb: 1 }}>
                        <Box
                            className="testRunTitleContainer"
                            sx={{ gap: 1, flexWrap: "wrap" }}
                        >
                            {onBack ? (
                                <IconButton
                                    size="small"
                                    onClick={onBack}
                                    sx={{
                                        border: "1px solid #2D4356",
                                        backgroundColor: "#fff",
                                        color: "#2D4356",
                                        width: 32,
                                        height: 32,
                                        "&:hover": {
                                            backgroundColor: "#f5f7fb",
                                        },
                                    }}
                                >
                                    <ArrowBackSharpIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                            <Typography
                                className="testRunTitle"
                                variant="h6"
                            >
                                {testRun?.name || "Test Run"}{" "}
                                {testRun?.testCaseName || testCase?.name}
                            </Typography>
                            {testRun?.status ? (
                                <Box
                                    className="testRunStatsSummaryStatus"
                                    sx={{
                                        height: 24,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        px: 1.5,
                                        borderRadius: "100px",
                                        textTransform: "capitalize",
                                        fontSize: "13px",
                                        ...statusStyles[testRun.status],
                                    }}
                                >
                                    {renderStatusLabel(testRun.status)}
                                </Box>
                            ) : null}
                        </Box>
                        {onOpenInNewTab && testRun?.id ? (
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleOpenInNewTab}
                            >
                                Open in new tab
                            </Button>
                        ) : null}
                    </Box>
                )}
                <Box className="testRunMiddleSection">
                    <Box className="testRunInfoContainer">
                        <PageLink link={testRun?.pageUrl || testCase?.pageUrl} />
                        <Box className="testRunPageDescriptionContainer">
                            <Typography variant="body1" color="primary">
                                <span className="testRunPageDescriptionLabel">
                                    Description:{" "}
                                </span>
                                {testRun?.description || testCase?.description || "No description"}
                            </Typography>
                        </Box>
                    </Box>
                    <Divider />
                    <Box className="testRunStatsContainer">
                        <StatsSummary
                            title="Test Version"
                            value={getTestRunVersionLabel(testRun)}
                            color="primary"
                        />
                        <StatsSummary
                            title="Status"
                            value={
                                isDraft ? (
                                    <span className="testRunStatsSummaryStatusContainer">
                                        <span
                                            className="testRunStatsSummaryStatus"
                                            style={{
                                                backgroundColor: "#9E9E9E",
                                                color: "#fff",
                                            }}
                                        >
                                            Draft
                                        </span>
                                    </span>
                                ) : testRun?.status ? (
                                    <span className="testRunStatsSummaryStatusContainer">
                                        <span
                                            className="testRunStatsSummaryStatus"
                                            style={{
                                                ...statusStyles[testRun.status],
                                            }}
                                        >
                                            {renderStatusLabel(testRun.status)}
                                        </span>
                                    </span>
                                ) : (
                                    ""
                                )
                            }
                            color="primary"
                        />
                        <StatsSummary
                            title="Test Cycle"
                            value={getTestRunCycleLabel(testRun)}
                            color="primary"
                        />
                        <StatsSummary
                            title="Created by"
                            value={testRun?.created_by || "-"}
                            color="primary"
                        />
                        <StatsSummary
                            title="Date and Time"
                            value={
                                testRun?.queued_at
                                    ? formatDate(testRun?.queued_at)
                                    : ""
                            }
                            color="primary"
                        />
                    </Box>
                    <Divider />
                </Box>
                {hasFinished && testRun?.steps?.length ? (
                    <Box className="testRunResultsSection">
                        <Box className="testRunResultsSectionTitle">
                            Results
                        </Box>
                        <Box className="testRunResultsSectionContent">
                            <TableContainer
                                className="testRunResultsTableContainer"
                                component={Paper}
                                elevation={0}
                            >
                                <Table className="testRunResultsTable">
                                    <TableHead className="testRunResultsTableHeader">
                                        <TableRow>
                                            <TableCell className="testRunResultsTableHeaderCell stepIndexHeaderCell">
                                                #
                                            </TableCell>
                                            <TableCell className="testRunResultsTableHeaderCell stepNameHeaderCell">
                                                Step Name
                                            </TableCell>
                                            <TableCell className="testRunResultsTableHeaderCell stepDataHeaderCell">
                                                Test Data
                                            </TableCell>
                                            <TableCell className="testRunResultsTableHeaderCell resultHeaderCell"></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <RunSteps
                                            steps={testRun.steps}
                                            activeStep={activeStep}
                                            setActiveStep={setActiveStep}
                                            handleSeek={handleSeek}
                                        />
                                        <TestRunAssertion testRun={testRun} />
                                        {testRun.evaluatuion_conclusions ? (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Box
                                                        className="testRunResultsAIConclusionContainer"
                                                        sx={{ mt: 2, pt: 1 }}
                                                    >
                                                        <Typography
                                                            className="testRunResultsAIConclusionContent"
                                                            variant="body1"
                                                            color="primary"
                                                        >
                                                            <span className="testRunResultsAIConclusionLabel">
                                                                AI Conclusion:{" "}
                                                            </span>
                                                            <span className="testRunResultsAIConclusionText">
                                                                {
                                                                    testRun.evaluatuion_conclusions
                                                                }
                                                            </span>
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : null}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box className="testRunResultsContentTabs">
                                <TabContext value={activeTabValue}>
                                    <Box className="testRunResultsTabsTogglerContainer">
                                        <TabList
                                            className="testRunResultsTabsToggler"
                                            onChange={handleChangeActiveTab}
                                        >
                                            <Tab
                                                className="testRunResultsTabLabelContainer"
                                                label={
                                                    <Box
                                                        className="testRunResultsTabLabel"
                                                        sx={{
                                                            color:
                                                                activeTabValue ===
                                                                    "1"
                                                                    ? "#4574D2"
                                                                    : "rgba(0, 0, 0, 0.6)",
                                                        }}
                                                    >
                                                        <PlayCircleIcon
                                                            className="testRunResultsTabLabelIcon"
                                                            color={
                                                                activeTabValue ===
                                                                    "1"
                                                                    ? "#4574D2"
                                                                    : "rgba(0, 0, 0, 0.6)"
                                                            }
                                                        />
                                                        <span className="testRunResultsTabLabelText">
                                                            VIEW
                                                        </span>
                                                    </Box>
                                                }
                                                value="1"
                                            />
                                            <Tab
                                                className="testRunResultsTabLabelContainer"
                                                label={
                                                    <Box
                                                        className="testRunResultsTabLabel"
                                                        sx={{
                                                            color:
                                                                activeTabValue ===
                                                                    "2"
                                                                    ? "#4574D2"
                                                                    : "rgba(0, 0, 0, 0.6)",
                                                        }}
                                                    >
                                                        <ArticleOutlinedIcon
                                                            className="testRunResultsTabLabelIcon"
                                                            color={
                                                                activeTabValue ===
                                                                    "2"
                                                                    ? "#4574D2"
                                                                    : "rgba(0, 0, 0, 0.6)"
                                                            }
                                                        />
                                                        <span className="testRunResultsTabLabelText">
                                                            Trace Report (Logs)
                                                        </span>
                                                    </Box>
                                                }
                                                value="2"
                                            />
                                            {showDebug &&
                                                testRun?.discovery_debug?.llm_logs
                                                    ?.length > 0 ? (
                                                <Tab
                                                    className="testRunResultsTabLabelContainer"
                                                    label={
                                                        <Box
                                                            className="testRunResultsTabLabel"
                                                            sx={{
                                                                color:
                                                                    activeTabValue ===
                                                                        "3"
                                                                        ? "#4574D2"
                                                                        : "rgba(0, 0, 0, 0.6)",
                                                            }}
                                                        >
                                                            <ArticleOutlinedIcon
                                                                className="testRunResultsTabLabelIcon"
                                                                color={
                                                                    activeTabValue ===
                                                                        "3"
                                                                        ? "#4574D2"
                                                                        : "rgba(0, 0, 0, 0.6)"
                                                                }
                                                            />
                                                            <span className="testRunResultsTabLabelText">
                                                                LLM I/O
                                                            </span>
                                                        </Box>
                                                    }
                                                    value="3"
                                                />
                                            ) : null}
                                        </TabList>
                                    </Box>
                                    <TabPanel
                                        value="1"
                                        className="testRunResultsTabContentContainer"
                                    >
                                        <VideoPlayer
                                            ref={videoRef}
                                            videoSrc={testRun?.videoUrl}
                                        />
                                    </TabPanel>
                                    <TabPanel
                                        value="2"
                                        className="testRunResultsTabContentContainer"
                                    >
                                        <TraceTab
                                            traceUrl={testRun?.aws_tracing_url}
                                        />
                                    </TabPanel>
                                    {showDebug &&
                                        testRun?.discovery_debug?.llm_logs?.length >
                                        0 ? (
                                        <TabPanel
                                            value="3"
                                            className="testRunResultsTabContentContainer"
                                        >
                                            {debugSummary ? (
                                                <Box
                                                    sx={{
                                                        mb: 2,
                                                        p: 1.5,
                                                        border: "1px solid #E3E8EF",
                                                        borderRadius: 2,
                                                        backgroundColor: "#F7F9FC",
                                                    }}
                                                >
                                                    <Typography fontWeight={600}>
                                                        Gemini cost
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Total:{" "}
                                                        {formatUsdCost(
                                                            debugSummary.totalUsd
                                                        )}{" "}
                                                        {debugSummary.currency}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Tokens:{" "}
                                                        {formatTokenCount(
                                                            debugSummary.inputTokens
                                                        )}{" "}
                                                        in /{" "}
                                                        {formatTokenCount(
                                                            debugSummary.outputTokens
                                                        )}{" "}
                                                        out
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Priced calls:{" "}
                                                        {formatTokenCount(
                                                            debugSummary.pricedCalls
                                                        )}
                                                    </Typography>
                                                </Box>
                                            ) : null}
                                            <TableContainer component={Paper}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>
                                                                #
                                                            </TableCell>
                                                            <TableCell>
                                                                Route
                                                            </TableCell>
                                                            <TableCell>
                                                                Cost (USD)
                                                            </TableCell>
                                                            <TableCell>
                                                                Request JSON
                                                            </TableCell>
                                                            <TableCell>
                                                                Response JSON
                                                            </TableCell>
                                                            <TableCell>
                                                                Timestamp
                                                            </TableCell>
                                                            <TableCell>
                                                                Image
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {testRun.discovery_debug.llm_logs.map(
                                                            (row, idx) => (
                                                                <TableRow
                                                                    key={idx}
                                                                >
                                                                    <TableCell>
                                                                        {idx +
                                                                            1}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {
                                                                            row.route
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {formatUsdCost(
                                                                            row.llm_cost_usd
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {row.request_json ? (
                                                                            <a
                                                                                href={
                                                                                    row.request_json
                                                                                }
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                            >
                                                                                open
                                                                            </a>
                                                                        ) : (
                                                                            "-"
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {row.response_json ? (
                                                                            <a
                                                                                href={
                                                                                    row.response_json
                                                                                }
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                            >
                                                                                open
                                                                            </a>
                                                                        ) : (
                                                                            "-"
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {row.response_timestamp
                                                                            ? formatDate(
                                                                                row.response_timestamp
                                                                            )
                                                                            : row.request_timestamp
                                                                                ? formatDate(
                                                                                    row.request_timestamp
                                                                                )
                                                                                : ""}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {row.image ? (
                                                                            <a
                                                                                href={
                                                                                    row.image
                                                                                }
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                            >
                                                                                image
                                                                            </a>
                                                                        ) : (
                                                                            "-"
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </TabPanel>
                                    ) : null}
                                </TabContext>
                            </Box>
                        </Box>
                    </Box>
                ) : isDraft ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 6,
                            px: 3,
                            textAlign: "center",
                        }}
                    >
                        <ArticleOutlinedIcon sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5 }} />
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mt: 2, fontWeight: 500 }}
                        >
                            This test is in draft state. Run the test to see the results.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ mt: 2, width: "100%" }}>
                        <Typography mb={1}>Test case running...</Typography>
                        <LinearProgress color="secondary" />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default EmbeddedTestRun;
